/**
 * Adjunction diagrams: unit/counit, the induced monad, and the
 * triangle identities.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useId } from "react";
import { GRAY } from "../../../design-system/tokens.js";
import {
  DIAGRAM_MONO,
  STROKE,
  SUBTLE,
  LABEL,
  ArrowDefs,
  ObjectNode,
  Arrow,
  FunctorBox,
} from "./primitives.js";

/**
 * Adjunction picture: F : C ⇄ D : G with unit η and counit ε.
 *
 *   C ──── F ────▶ D
 *   ▲              │
 *   │  G           │ ε
 *   └──────────────┘
 *
 * The picture privileges legibility over pedantic 2-categorical accuracy —
 * it is meant for an HTML page, not a paper.
 */
export function AdjunctionDiagram({
  leftCat = "C",
  rightCat = "D",
  leftAdjoint = "F",
  rightAdjoint = "G",
  accent = STROKE,
  ariaLabel = "Adjunction F ⊣ G between C and D with unit and counit",
}: {
  readonly leftCat?: string;
  readonly rightCat?: string;
  readonly leftAdjoint?: string;
  readonly rightAdjoint?: string;
  readonly accent?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  return (
    <svg
      viewBox="0 0 320 180"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />
      <ObjectNode cx={56} cy={90} label={leftCat} accent={accent} />
      <ObjectNode cx={264} cy={90} label={rightCat} accent={accent} />
      <Arrow
        x1={70}
        y1={80}
        x2={250}
        y2={80}
        label={leftAdjoint}
        markerId={markerId}
        color={accent}
        curve={26}
      />
      <Arrow
        x1={250}
        y1={100}
        x2={70}
        y2={100}
        label={rightAdjoint}
        markerId={markerId}
        color={accent}
        curve={-26}
      />
      <text
        x={160}
        y={26}
        textAnchor="middle"
        fontSize="13"
        fontFamily={DIAGRAM_MONO}
        fontWeight={600}
        fill={LABEL}
      >
        η : Id
        <tspan baselineShift="sub" fontSize="9">
          C
        </tspan>{" "}
        ⇒ G ∘ F
      </text>
      <text
        x={160}
        y={170}
        textAnchor="middle"
        fontSize="13"
        fontFamily={DIAGRAM_MONO}
        fontWeight={600}
        fill={LABEL}
      >
        ε : F ∘ G ⇒ Id
        <tspan baselineShift="sub" fontSize="9">
          D
        </tspan>
      </text>
      <text x={160} y={132} textAnchor="middle" fontSize="14" fontWeight={700} fill={accent}>
        F ⊣ G
      </text>
    </svg>
  );
}

/**
 * AdjunctionToMonadDiagram: shows how an adjunction F ⊣ G induces both a
 * monad T = G ∘ F on C and a comonad W = F ∘ G on D, with their unit/counit
 * inherited from η and ε.
 *
 *      F
 *   C ───▶ D            T = G ∘ F   (monad on C, η as unit, GεF as μ)
 *   ◀───  G             W = F ∘ G   (comonad on D, ε as counit, FηG as δ)
 */
export function AdjunctionToMonadDiagram({
  accent = STROKE,
  ariaLabel = "An adjunction F ⊣ G induces a monad T = G ∘ F on C and a comonad W = F ∘ G on D",
}: {
  readonly accent?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  return (
    <svg
      viewBox="0 0 420 200"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />
      <ObjectNode cx={56} cy={100} label="C" accent={accent} />
      <ObjectNode cx={232} cy={100} label="D" accent={accent} />
      <Arrow
        x1={70}
        y1={90}
        x2={218}
        y2={90}
        label="F"
        markerId={markerId}
        color={accent}
        curve={26}
      />
      <Arrow
        x1={218}
        y1={110}
        x2={70}
        y2={110}
        label="G"
        markerId={markerId}
        color={accent}
        curve={-26}
      />
      <text x={144} y={140} textAnchor="middle" fontSize="13" fontWeight={700} fill={accent}>
        F ⊣ G
      </text>
      <text
        x={328}
        y={70}
        textAnchor="start"
        fontSize="12"
        fontFamily={DIAGRAM_MONO}
        fontWeight={600}
        fill={LABEL}
      >
        T = G ∘ F
      </text>
      <text x={328} y={86} textAnchor="start" fontSize="11" fill={GRAY[600]}>
        monad on C
      </text>
      <text
        x={328}
        y={124}
        textAnchor="start"
        fontSize="12"
        fontFamily={DIAGRAM_MONO}
        fontWeight={600}
        fill={LABEL}
      >
        W = F ∘ G
      </text>
      <text x={328} y={140} textAnchor="start" fontSize="11" fill={GRAY[600]}>
        comonad on D
      </text>
      <text
        x={144}
        y={32}
        textAnchor="middle"
        fontSize="11"
        fontFamily={DIAGRAM_MONO}
        fill={GRAY[600]}
      >
        η : Id
        <tspan baselineShift="sub" fontSize="9">
          C
        </tspan>{" "}
        ⇒ G ∘ F &nbsp;|&nbsp; μ = G ε F
      </text>
      <text
        x={144}
        y={178}
        textAnchor="middle"
        fontSize="11"
        fontFamily={DIAGRAM_MONO}
        fill={GRAY[600]}
      >
        ε : F ∘ G ⇒ Id
        <tspan baselineShift="sub" fontSize="9">
          D
        </tspan>{" "}
        &nbsp;|&nbsp; δ = F η G
      </text>
    </svg>
  );
}

/**
 * TriangleIdentityDiagram: the two triangle identities that pin the unit
 * and counit of an adjunction `F ⊣ G` together.
 *
 * Triangle "left" (drawn on the left half of the picture):
 *
 *           Fη            εF
 *      F ───────▶ F G F ──────▶ F           = id_F
 *
 * Triangle "right" (drawn on the right half):
 *
 *           ηG            Gε
 *      G ───────▶ G F G ──────▶ G           = id_G
 *
 * Each triangle is rendered as a horizontal cycle that visibly starts and
 * ends at the same node — that is the geometric meaning of "= id".
 *
 * Hover/focus highlights are intentionally opt-in: a host passes
 * `highlighted="Fη" | "εF" | "ηG" | "Gε" | undefined` and the diagram
 * thickens / re-colors only the selected leg. That lets the explainer wire
 * its own legend without re-implementing the geometry.
 */
export type TriangleIdentityLeg = "Fη" | "εF" | "ηG" | "Gε";

export function TriangleIdentityDiagram({
  accent = STROKE,
  highlighted,
  ariaLabel = "Triangle identities: (εF) ∘ (Fη) = id_F and (Gε) ∘ (ηG) = id_G",
}: {
  readonly accent?: string;
  readonly highlighted?: TriangleIdentityLeg | undefined;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  const subtleMarkerId = `${markerId}-subtle`;
  // Two horizontal triangles arranged left/right of one another.
  // The left triangle is F → FGF → F. The right is G → GFG → G.
  const colorOf = (leg: TriangleIdentityLeg, base = accent): string =>
    highlighted === undefined || highlighted === leg ? base : SUBTLE;
  const widthOf = (leg: TriangleIdentityLeg): number => (highlighted === leg ? 2.5 : 1.5);
  return (
    <svg
      viewBox="0 0 560 220"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />
      <ArrowDefs id={subtleMarkerId} color={GRAY[500]} />
      {/* Left triangle: F ──Fη─▶ FGF ──εF─▶ F  =  id_F */}
      <text
        x={140}
        y={28}
        textAnchor="middle"
        fontSize="12"
        fontFamily={DIAGRAM_MONO}
        fontWeight={700}
        fill={LABEL}
      >
        (εF) ∘ (Fη) = id
        <tspan baselineShift="sub" fontSize="9">
          F
        </tspan>
      </text>
      <ObjectNode cx={36} cy={120} label="F" accent={accent} />
      <FunctorBox cx={140} cy={120} label="GF" functor="F" accent={accent} />
      <ObjectNode cx={244} cy={120} label="F" accent={accent} />
      <g opacity={highlighted === undefined || highlighted === "Fη" ? 1 : 0.5}>
        <path
          d={`M 50 120 Q 95 70 116 110`}
          fill="none"
          stroke={colorOf("Fη")}
          strokeWidth={widthOf("Fη")}
          markerEnd={`url(#${markerId}-head)`}
        />
        <text
          x={86}
          y={70}
          textAnchor="middle"
          fontSize="13"
          fontFamily={DIAGRAM_MONO}
          fontWeight={highlighted === "Fη" ? 700 : 500}
          fill={colorOf("Fη", LABEL)}
        >
          Fη
        </text>
      </g>
      <g opacity={highlighted === undefined || highlighted === "εF" ? 1 : 0.5}>
        <path
          d={`M 164 110 Q 195 70 230 120`}
          fill="none"
          stroke={colorOf("εF")}
          strokeWidth={widthOf("εF")}
          markerEnd={`url(#${markerId}-head)`}
        />
        <text
          x={200}
          y={70}
          textAnchor="middle"
          fontSize="13"
          fontFamily={DIAGRAM_MONO}
          fontWeight={highlighted === "εF" ? 700 : 500}
          fill={colorOf("εF", LABEL)}
        >
          εF
        </text>
      </g>
      <Arrow
        x1={50}
        y1={134}
        x2={228}
        y2={134}
        label="id"
        subLabel="F"
        markerId={subtleMarkerId}
        color={GRAY[500]}
        variant="dashed"
      />
      {/* Right triangle: G ──ηG─▶ FGF ──Gε─▶ G  =  id_G */}
      <text
        x={420}
        y={28}
        textAnchor="middle"
        fontSize="12"
        fontFamily={DIAGRAM_MONO}
        fontWeight={700}
        fill={LABEL}
      >
        (Gε) ∘ (ηG) = id
        <tspan baselineShift="sub" fontSize="9">
          G
        </tspan>
      </text>
      <ObjectNode cx={316} cy={120} label="G" accent={accent} />
      <FunctorBox cx={420} cy={120} label="FG" functor="G" accent={accent} />
      <ObjectNode cx={524} cy={120} label="G" accent={accent} />
      <g opacity={highlighted === undefined || highlighted === "ηG" ? 1 : 0.5}>
        <path
          d={`M 330 120 Q 375 70 396 110`}
          fill="none"
          stroke={colorOf("ηG")}
          strokeWidth={widthOf("ηG")}
          markerEnd={`url(#${markerId}-head)`}
        />
        <text
          x={366}
          y={70}
          textAnchor="middle"
          fontSize="13"
          fontFamily={DIAGRAM_MONO}
          fontWeight={highlighted === "ηG" ? 700 : 500}
          fill={colorOf("ηG", LABEL)}
        >
          ηG
        </text>
      </g>
      <g opacity={highlighted === undefined || highlighted === "Gε" ? 1 : 0.5}>
        <path
          d={`M 444 110 Q 475 70 510 120`}
          fill="none"
          stroke={colorOf("Gε")}
          strokeWidth={widthOf("Gε")}
          markerEnd={`url(#${markerId}-head)`}
        />
        <text
          x={480}
          y={70}
          textAnchor="middle"
          fontSize="13"
          fontFamily={DIAGRAM_MONO}
          fontWeight={highlighted === "Gε" ? 700 : 500}
          fill={colorOf("Gε", LABEL)}
        >
          Gε
        </text>
      </g>
      <Arrow
        x1={330}
        y1={134}
        x2={508}
        y2={134}
        label="id"
        subLabel="G"
        markerId={subtleMarkerId}
        color={GRAY[500]}
        variant="dashed"
      />
      <text x={280} y={210} textAnchor="middle" fontSize="11" fill={GRAY[600]}>
        Going through the unit and the counit on the appropriate side must equal doing nothing.
      </text>
    </svg>
  );
}
